import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Save } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { cn } from '../../../lib/utils'
import PhotoUploader from './PhotoUploader'

const MUSCLE_GROUPS = ['chest','back','shoulders','biceps','triceps','quads','hamstrings','glutes','core','full_body']
const EQUIPMENT_TYPES = ['barbell','dumbbell','machine','cable','bodyweight','smith']

// Used for both create + edit. On create the parent passes a fresh uuid as
// `idForUpload` so PhotoUploader can use a stable storage path before the
// exercise row exists.
export default function ExerciseForm({
  initialValues,
  idForUpload,
  currentPhotoUrl,
  onSubmit,
  onCancel,
  submitting,
}) {
  const { t } = useTranslation()
  const [name, setName] = useState(initialValues?.name || '')
  const [muscleGroup, setMuscleGroup] = useState(initialValues?.muscle_group || 'chest')
  const [equipmentType, setEquipmentType] = useState(initialValues?.equipment_type || 'barbell')
  const [setupNotes, setSetupNotes] = useState(initialValues?.setup_notes || '')
  const [photoPath, setPhotoPath] = useState(initialValues?.machine_photo_url ?? null)
  const [photoUrl, setPhotoUrl] = useState(currentPhotoUrl || null)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!name.trim()) return
    onSubmit({
      name: name.trim(),
      muscle_group: muscleGroup,
      equipment_type: equipmentType,
      setup_notes: setupNotes.trim() || null,
      machine_photo_url: photoPath,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('workout.exercises.form.name')}
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-12 sm:h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm focus:outline-none"
          required
          maxLength={80}
        />
      </div>

      {/* Muscle group */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('workout.exercises.form.muscleGroup')}
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
          {MUSCLE_GROUPS.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setMuscleGroup(g)}
              className={cn(
                'h-9 rounded-md text-xs font-medium transition-colors',
                muscleGroup === g
                  ? 'bg-app-primary-500 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              {t(`workout.muscleGroup.${g}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Equipment */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('workout.exercises.form.equipmentType')}
        </label>
        <div className="grid grid-cols-3 gap-1.5">
          {EQUIPMENT_TYPES.map((eq) => (
            <button
              key={eq}
              type="button"
              onClick={() => setEquipmentType(eq)}
              className={cn(
                'h-9 rounded-md text-xs font-medium transition-colors',
                equipmentType === eq
                  ? 'bg-app-primary-500 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              {t(`workout.equipment.${eq}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Photo */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('workout.exercises.form.photo')}
        </label>
        <PhotoUploader
          exerciseId={idForUpload}
          currentUrl={photoUrl}
          onUploaded={(path) => {
            setPhotoPath(path)
            // Best-effort: update the preview to a cache-busted path-derived URL.
            // The actual public URL gets resolved on the next list refetch.
          }}
          onCleared={() => {
            setPhotoPath(null)
            setPhotoUrl(null)
          }}
        />
      </div>

      {/* Setup notes */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('workout.exercises.form.setupNotes')}
        </label>
        <textarea
          value={setupNotes}
          onChange={(e) => setSetupNotes(e.target.value)}
          placeholder={t('workout.exercises.form.setupPlaceholder')}
          rows={3}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none resize-y"
        />
      </div>

      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            {t('common.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          disabled={submitting || !name.trim()}
          className="bg-app-primary-500 hover:bg-app-primary-600 text-white"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin me-1.5" /> : <Save className="w-4 h-4 me-1.5" />}
          {t('workout.exercises.form.save')}
        </Button>
      </div>
    </form>
  )
}
